import { useEffect, useState, type ReactNode } from 'react';
import { HStack, Input, NativeSelect, Stack, Switch, Text } from '@chakra-ui/react';
import type { Bridge, DeviceInfo } from '@casa/bridge';
import { applyAppSettings, DIM_MINUTES, readAppSettings, writeAppSettings, type AppSettings } from '../data/app_settings.ts';
import { ChromeNote } from '../ui/chrome_page.tsx';
import { goToSettings } from './settings_navigation.ts';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';
import { UpdatesGroup } from './updates_group.tsx';

// "App settings" (docs/specs/settings.md §4.4): what belongs to this device. The screen: keep awake
// (AP-1), sleep times (AP-3), dimming when idle (AP-5) and someone near waking it (AP-2); the app's
// updates (app-updates AU-28); the drivers (UI-3).

function SettingSwitch({ label, checked, disabled = false, onChange }: { label: string; checked: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) : ReactNode {
    return (
        <Switch.Root checked={checked} disabled={disabled} onCheckedChange={(details) => onChange(details.checked)} aria-label={label}>
            <Switch.HiddenInput />
            <Switch.Control />
        </Switch.Root>
    );
}

function TimeField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) : ReactNode {
    return (
        <HStack gap="2">
            <Text fontSize="sm" color="fg.muted">
                {label}
            </Text>
            <Input
                type="time"
                size="sm"
                width="auto"
                aria-label={`Sleep ${label.toLowerCase()}`}
                value={value}
                onChange={(event) => {
                    if (event.target.value !== '') {
                        onChange(event.target.value);
                    }
                }}
            />
        </HStack>
    );
}

/** What notices someone near, as the row says it (AP-2). */
function approachDetail(device: DeviceInfo) : string {
    return device.sensors?.includes('proximity')
        ? 'With the proximity sensor: a hand close over it'
        : 'With the light sensor: a hand over it, or someone close in front';
}

export function AppPart({ shell, onChanged }: { shell: Bridge; onChanged?: () => void }) : ReactNode {
    const [settings, setSettings] = useState<AppSettings>();
    const [device, setDevice] = useState<DeviceInfo>();
    const [problem, setProblem] = useState<string>();

    useEffect(() => {
        void readAppSettings(shell).then(setSettings);
        shell.device.info().then(setDevice, () => setDevice(undefined));
    }, [shell]);

    const change = async (next: AppSettings) : Promise<void> => {
        setSettings(next);
        await writeAppSettings(shell, next);
        setProblem(await applyAppSettings(shell, next));
        onChanged?.();
    };

    if (!settings) {
        return null;
    }

    // Sleep times and dimming need the screen kept on (AP-7).
    const canSleep = settings.keepAwake;
    const hasSensor = (device?.sensors?.length ?? 0) > 0;
    const notes = [
        canSleep ? undefined : 'Sleep times and dimming need keep awake: without it the system turns the screen off itself.',
        device?.brightness === true ? undefined : "Tilez can't change this screen's brightness: it darkens what it shows instead.",
    ].filter((note) => note !== undefined);

    return (
        <Stack gap="6">
            <SettingsGroup title="Screen" note={notes.length > 0 ? notes.join(' ') : undefined}>
                <SettingsRow
                    icon="lightbulb"
                    title="Keep awake"
                    detail="The screen stays on while Tilez is shown"
                    end={<SettingSwitch label="Keep awake" checked={settings.keepAwake} onChange={(checked) => void change({ ...settings, keepAwake: checked })} />}
                    data-testid="keep-awake"
                />
                <SettingsRow
                    icon="moon"
                    title="Sleep times"
                    detail={settings.sleep.enabled ? `Blank from ${settings.sleep.from} to ${settings.sleep.to}; a tap wakes it for a minute` : 'Blank the screen at night'}
                    end={<SettingSwitch label="Sleep times" checked={settings.sleep.enabled && canSleep} disabled={!canSleep} onChange={(checked) => void change({ ...settings, sleep: { ...settings.sleep, enabled: checked } })} />}
                />
                {settings.sleep.enabled && canSleep ? (
                    <HStack gap="4" px="4" py="3" flexWrap="wrap">
                        <TimeField label="From" value={settings.sleep.from} onChange={(from) => void change({ ...settings, sleep: { ...settings.sleep, from } })} />
                        <TimeField label="To" value={settings.sleep.to} onChange={(to) => void change({ ...settings, sleep: { ...settings.sleep, to } })} />
                    </HStack>
                ) : null}
                <SettingsRow
                    icon="circle-half-stroke"
                    title="Dim when idle"
                    detail={settings.dim.enabled ? `After ${settings.dim.minutes} ${settings.dim.minutes === 1 ? 'minute' : 'minutes'} without a touch` : 'Dim the screen when no one uses it'}
                    end={
                        <HStack gap="3">
                            {settings.dim.enabled && canSleep ? (
                                <NativeSelect.Root size="sm" width="auto">
                                    <NativeSelect.Field aria-label="Dim after" value={String(settings.dim.minutes)} onChange={(event) => void change({ ...settings, dim: { ...settings.dim, minutes: Number(event.target.value) } })}>
                                        {DIM_MINUTES.map((minutes) => (
                                            <option key={minutes} value={minutes}>
                                                {minutes} min
                                            </option>
                                        ))}
                                    </NativeSelect.Field>
                                    <NativeSelect.Indicator />
                                </NativeSelect.Root>
                            ) : null}
                            <SettingSwitch label="Dim when idle" checked={settings.dim.enabled && canSleep} disabled={!canSleep} onChange={(checked) => void change({ ...settings, dim: { ...settings.dim, enabled: checked } })} />
                        </HStack>
                    }
                />
                {device && hasSensor ? (
                    <SettingsRow
                        icon="hand"
                        title="Wake when someone comes near"
                        detail={approachDetail(device)}
                        end={<SettingSwitch label="Wake when someone comes near" checked={settings.wakeOnApproach && canSleep} disabled={!canSleep} onChange={(checked) => void change({ ...settings, wakeOnApproach: checked })} />}
                    />
                ) : null}
            </SettingsGroup>
            {problem ? <ChromeNote status="warning" text={`The screen can't be kept on here: ${problem}.`} /> : null}
            <UpdatesGroup shell={shell} />
            <SettingsGroup title="Drivers">
                <SettingsRow icon="plug" title="Drivers" detail="Built-in and installed drivers" onClick={() => goToSettings('drivers')} />
            </SettingsGroup>
        </Stack>
    );
}
